/**
 * SandboxModeBanner
 * -----------------
 * Persistent top banner that appears when the current session is a
 * customer-journey sandbox (user.is_sandbox === true).  Makes it
 * impossible to forget you're in a preview window: red bar with the
 * persona, expiry countdown, and a "Sign out" CTA.
 *
 * Mounted globally in App.js so it overlays every route.
 */
import { useEffect, useState } from "react";
import { AlertTriangle, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function fmtCountdown(expiresIso) {
    if (!expiresIso) return null;
    const ms = new Date(expiresIso).getTime() - Date.now();
    if (ms <= 0) return "expired";
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    if (h > 0) return `${h}h ${m}m left`;
    return `${m}m left`;
}

export default function SandboxModeBanner() {
    const { user, logout } = useAuth();
    const [, setTick] = useState(0);

    // Re-render every minute so the countdown stays fresh.
    useEffect(() => {
        const id = setInterval(() => setTick((n) => n + 1), 60000);
        return () => clearInterval(id);
    }, []);

    if (!user?.is_sandbox) return null;
    const personaLabel = user.sandbox_persona === "adult"
        ? "Adult athlete (self)"
        : user.sandbox_persona === "parent_one"
        ? "Parent w/ one kid"
        : user.sandbox_persona === "parent_many"
        ? "Parent w/ multiple kids"
        : user.sandbox_persona === "coach"
        ? "New coach hire"
        : "Sandbox";
    const countdown = fmtCountdown(user.expires_at);

    const onExit = async () => {
        try { await logout(); } catch { /* noop */ }
        // Don't navigate — just close the window so Sachin's main tab is unaffected.
        try { window.close(); } catch { /* noop */ }
        window.location.replace("/");
    };

    return (
        <div
            data-testid="sandbox-mode-banner"
            className="sticky top-0 z-[80] bg-brand-red text-white border-b-2 border-red-800 shadow-lg"
        >
            <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    <span className="text-[10px] tracking-[0.25em] uppercase font-bold truncate">
                        Preview mode · {personaLabel}
                    </span>
                    {countdown && (
                        <span className="text-[10px] tracking-[0.2em] uppercase opacity-80 shrink-0">
                            · {countdown}
                        </span>
                    )}
                </div>
                <button
                    type="button"
                    data-testid="sandbox-exit-btn"
                    onClick={onExit}
                    className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase border border-white/40 px-2.5 py-1 hover:bg-white hover:text-brand-red transition-colors"
                >
                    <LogOut className="w-3 h-3" /> Exit
                </button>
            </div>
        </div>
    );
}
